import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Store } from "lucide-react";
import api from "@/lib/api";
import { errText, when } from "./shared";
import { MAP_BASE, StorePickerDialog, HistoryDialog, RemoveConfirm } from "./StoreMatchDialogs";

const STATUS = {
  VERIFIED: ["Verified", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  UNVERIFIED: ["Unverified", "bg-amber-50 text-amber-800 border-amber-200"],
  NO_MATCH: ["No match", "bg-slate-100 text-slate-600 border-slate-200"],
  NOT_REVIEWED: ["Not reviewed", "bg-white text-slate-500 border-slate-200"],
};

function MatchChip({ status, testid }) {
  const [label, tone] = STATUS[status || "NOT_REVIEWED"];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-widest ${tone}`} data-testid={testid}>{label}</span>;
}

function Target({ row }) {
  if (!row.merchant) return <div className="text-xs text-slate-500" data-testid={`gc-map-target-${row.product_id}`}>{row.status === "NO_MATCH" ? "No matching Cyannect storefront" : "Not matched yet"}</div>;
  return (
    <div className="text-xs text-slate-700" data-testid={`gc-map-target-${row.product_id}`}>
      <b className="text-slate-900">{row.merchant.name || "Merchant"}</b>
      {row.storefront && <> → {row.storefront.name} <span className="text-slate-500">({row.storefront.status})</span></>}
      <span className="ml-2 text-slate-500" data-testid={`gc-map-offers-${row.product_id}`}>{row.live_offer_count} live offer{row.live_offer_count === 1 ? "" : "s"}</span>
      {!row.store_visible_to_shoppers && <span className="ml-2 text-rose-700 font-semibold">hidden from shoppers</span>}
    </div>
  );
}

function Btn({ children, onClick, disabled, testid, tone = "border-cyan-200 text-cyan-800 hover:bg-cyan-50" }) {
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className={`text-xs px-3 py-1 rounded-full border font-semibold transition-colors disabled:opacity-40 ${tone}`}>{children}</button>;
}

function MappingRow({ row, canEdit, busy, act, onChange, onHistory, onRemove }) {
  const id = row.product_id;
  const s = row.suggestion;
  return (
    <li className="py-3 flex flex-wrap items-start justify-between gap-3" data-testid={`gc-map-row-${id}`}>
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">{row.brand} <MatchChip status={row.status} testid={`gc-map-status-${id}`} /></div>
        <Target row={row} />
        {row.legacy && <div className="text-[11px] text-amber-800" data-testid={`gc-map-legacy-${id}`}>Carried over from offer link · {row.legacy.offer_name || row.legacy.campaign_id}</div>}
        {row.status === "VERIFIED" && <div className="text-[11px] text-slate-500" data-testid={`gc-map-verified-${id}`}>Verified by {row.verified_by_email || "admin"} · {when(row.verified_at)}</div>}
        {canEdit && s && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50/50 px-2 py-1 text-xs" data-testid={`gc-map-suggestion-${id}`}>
            <span className="text-[10px] uppercase tracking-widest font-semibold text-cyan-800">Suggested</span>
            <b className="text-slate-800">{s.store_name || "Merchant"}</b>
            <span className="text-slate-500">{s.reason_label}</span>
            <button disabled={busy} onClick={() => act("verify", row, { store_id: s.store_id, storefront_id: s.storefront_id })}
                    className="pf-btn-primary px-3 py-0.5 rounded-full text-[11px] font-semibold disabled:opacity-50" data-testid={`gc-map-accept-${id}`}>Verify</button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {canEdit && row.status === "UNVERIFIED" && <Btn disabled={busy} onClick={() => act("verify", row)} testid={`gc-map-verify-${id}`} tone="border-emerald-300 text-emerald-800 hover:bg-emerald-50">Verify match</Btn>}
        {canEdit && <Btn onClick={() => onChange(row)} testid={`gc-map-change-${id}`}>{row.merchant ? "Change match" : "Match"}</Btn>}
        {canEdit && row.status !== "NO_MATCH" && <Btn disabled={busy} onClick={() => onRemove(row)} testid={`gc-map-remove-${id}`} tone="border-rose-200 text-rose-700 hover:bg-rose-50">{row.merchant ? "Remove match" : "No match"}</Btn>}
        <Btn onClick={() => onHistory(row)} testid={`gc-map-history-${id}`} tone="border-slate-200 text-slate-600 hover:bg-slate-50">History</Btn>
      </div>
    </li>
  );
}

/** Gift card brand → verified Cyannect merchant/storefront. Only VERIFIED matches show offers in My Gift Cards. */
export function BrandStoreMatching({ canEdit }) {
  const [data, setData] = useState({ items: [], counts: {} });
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(null);
  const [history, setHistory] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get(MAP_BASE)).data); } catch (e) { toast.error(errText(e, "Could not load brand matches")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (kind, row, body) => {
    setBusy(true);
    try {
      if (kind === "verify") await api.post(`${MAP_BASE}/${row.product_id}/verify`, body || {});
      else await api.delete(`${MAP_BASE}/${row.product_id}`);
      toast.success(kind === "verify" ? `${row.brand} match verified.` : `${row.brand} set to no match.`);
      load();
    } catch (e) { toast.error(errText(e, "Could not update the match")); }
    finally { setBusy(false); }
  };

  return (
    <div className="pf-card p-5" data-testid="gc-brand-store-matching">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><Store className="w-4 h-4 text-slate-500" /><h2 className="font-display text-lg text-slate-900">Brand ↔ merchant matching</h2></div>
        <div className="flex flex-wrap gap-1.5" data-testid="gc-map-counts">
          {Object.keys(STATUS).map((k) => <span key={k} className="text-[11px] text-slate-600"><MatchChip status={k} /> <b className="tabular-nums" data-testid={`gc-map-count-${k}`}>{data.counts?.[k] ?? 0}</b></span>)}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-2">Shoppers see a brand's live Cyannect offers inside My Gift Cards only after the match is <b>Verified</b>. Carried-over offer links start as Unverified.</p>
      <ul className="divide-y divide-slate-100">
        {data.items.map((r) => <MappingRow key={r.product_id} row={r} canEdit={canEdit} busy={busy} act={act} onChange={setChanging} onHistory={setHistory} onRemove={setRemoving} />)}
      </ul>
      <StorePickerDialog row={changing} onClose={() => setChanging(null)} onSaved={() => { setChanging(null); load(); }} />
      <HistoryDialog row={history} onClose={() => setHistory(null)} />
      <RemoveConfirm row={removing} onCancel={() => setRemoving(null)} onConfirm={() => { act("remove", removing); setRemoving(null); }} />
    </div>
  );
}
